/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { analytics } from "../../helpers/analytics";
import { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ICourseFields } from '../../@types/generated/contentful';
import { UserContext } from '../../context/userContext';
import { hex2rgba } from '../../helpers/shared';
import { getContentfulImageSrcSet, getContentfulImageUrl } from '../../helpers/image';

import { CourseModal } from '../CourseModal';

type Props = {
  course: ICourseFields;
};

const CourseCard = ({ course }: Props) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const { title, subtitle, numberOfHours, numberOfSection, illustration, colors, fontColor } = course;

  const [searchParams, setSearchParams] = useSearchParams();
  const illustrationImage = getContentfulImageUrl(illustration, { width: 520 });
  const illustrationSrcSet = getContentfulImageSrcSet(illustration, [320, 520, 760]);

  const MAX_PROGRESS_WIDTH = 98.5;
  const MIN_PROGRESS_WIDTH = 3;

  const { user } = useContext(UserContext);
  const { hasPremium = false } = user || {};

  const handleClick = () => {
    setModalOpen(true);
    setSearchParams({ course: title.replace(/\s/g, '_') });
    analytics.track('Academy Selected', {
      premium: hasPremium,
      academySelected: title,
      section_count: numberOfSection,
      duration_minutes: numberOfHours,
      progress_percent: progress,
    });
    analytics.track("Course Card Clicked", {
      premium: hasPremium,
      course: title,
      section_count: numberOfSection,
      duration_minutes: numberOfHours,
      progress_percent: progress,
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    searchParams.delete('course');
    setSearchParams(searchParams);
  };

  useEffect(() => {
    if (searchParams.get('course') === title.replace(/\s/g, '_')) {
      analytics.track("Course Deep Link Opened", {
        course: title,
      });
      setModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const keys = Object.keys(localStorage);

    const filteredKeys = keys.filter((key) => key.includes(title));

    if (filteredKeys.length > 0) {
      const times = filteredKeys.map((key) => localStorage.getItem(key));
      const result = times.reduce((sum, current) => sum + Number(current), 0);

      const timeInPrc = (result * 100) / (numberOfHours * 60);
      setProgress(
        timeInPrc > MAX_PROGRESS_WIDTH
          ? MAX_PROGRESS_WIDTH
          : timeInPrc < MIN_PROGRESS_WIDTH
          ? MIN_PROGRESS_WIDTH
          : timeInPrc
      );
    } else {
      setProgress(0);
    }
  }, [setProgress, numberOfHours, title, isModalOpen]);

  return (
    <div
      className={`w-[350px] 
      h-[240px] rounded-[12px] 
      relative flex items-end cursor-pointer
      md:w-[300px] md:h-[190px]
      `}
      style={{ backgroundImage: `linear-gradient(to top, ${colors[0]} 0%, ${colors[1]} 100%)`, color: fontColor }}
      onClick={handleClick}
    >
      {illustrationImage && (
        <img
          className='absolute z-[0] -translate-x-[50%] -translate-y-[50%] left-1/2 top-1/2 select-none max-h-full'
          src={illustrationImage}
          srcSet={illustrationSrcSet}
          sizes='(max-width: 768px) 300px, 350px'
          alt=''
          loading='lazy'
          decoding='async'
        />
      )}
      <div
        className='relative z-[1] p-6 w-full rounded-b-[12px]'
        style={{ backgroundImage: `linear-gradient(to top, ${colors[1]} 20%, ${hex2rgba(colors[1], 0)} 100%)` }}
      >
        <h3 className='text-2xl leading-2xl font-bold'>{title}</h3>
        <p className='text-sm leading-sm font-medium'>
          {numberOfSection} sections — {numberOfHours} minutes
        </p>
        <p className='text-sm leading-sm'>{subtitle}</p>
      </div>
      <div
        className={`h-[7px] absolute bottom-0 z-[2] left-[3px] bg-[#FF0000] rounded-bl-[16px] ${
          progress > 95 ? 'rounded-br-[16px]' : ''
        }
        `}
        style={{ width: `${progress}%` }}
      />
      <CourseModal isOpen={isModalOpen} onClose={handleModalClose} course={course} />
    </div>
  );
};

export { CourseCard };
